import { useCallback, useRef, useState } from 'react';
import { Backdrop } from '@material-ui/core';
import cx from 'classnames';
import PropTypes from 'prop-types';
import { useDebouncedCallback } from 'use-debounce';

import { ChevronIcon } from 'src/components/ChevronIcon';
import { CustomMenu } from 'src/components/CustomMenu';
import { GENERATION_IMPROVE_MODE } from 'src/data/generation';
import { ROTATE_DIRECTION } from 'src/data/ui';

import { InternalListItem } from '../InternalListItem';

import { useStyles } from './useStyles';

const SUBMENU_PROPS = {
  anchorOrigin: {
    vertical: 'top',
    horizontal: 'right',
  },
  transformOrigin: {
    vertical: 'top',
    horizontal: 'left',
  },
  getContentAnchorEl: null,
  autoFocus: false,
  disableAutoFocusItem: true,
  disableAutoFocus: true,
  disableEnforceFocus: true,
  disableRestoreFocus: true,
  disableScrollLock: true,
  disablePortal: false,
};

const HOVER_MENU_TIMEOUT = 100;

const propTypes = {
  className: PropTypes.string,
  classes: PropTypes.shape({}),
  improveMode: PropTypes.oneOf(Object.values(GENERATION_IMPROVE_MODE)).isRequired,
  labelOverride: PropTypes.string,
  children: PropTypes.func.isRequired,
  selected: PropTypes.bool,
  disabled: PropTypes.bool,
  disableMouseLeave: PropTypes.bool,
};

const defaultProps = {
  className: '',
  classes: null,
  labelOverride: '',
  selected: false,
  disabled: false,
  disableMouseLeave: false,
};

export const InternalSubmenuListItem = ({
  className,
  classes,
  improveMode,
  labelOverride,
  children,
  selected,
  disabled,
  disableMouseLeave = false,
}) => {
  const styles = useStyles({ classes });

  const [isSubmenuOpen, setIsSubmenuOpen] = useState(false);
  const menuItemRef = useRef(null);

  const debouncedOpen = useDebouncedCallback(() => {
    setIsSubmenuOpen(true);
  }, HOVER_MENU_TIMEOUT);

  const debouncedClose = useDebouncedCallback(() => {
    setIsSubmenuOpen(false);
  }, HOVER_MENU_TIMEOUT);

  const handleClick = useCallback(() => {
    if (disabled) {
      return;
    }

    debouncedClose.cancel();
    setIsSubmenuOpen(true);
  }, [disabled, debouncedClose]);

  const handleMouseEnter = useCallback(() => {
    if (disabled) {
      return;
    }

    debouncedClose.cancel();
    debouncedOpen();
  }, [disabled, debouncedOpen, debouncedClose]);

  const handleMouseLeave = useCallback(() => {
    if (disableMouseLeave) {
      return;
    }

    debouncedOpen.cancel();
    debouncedClose();
  }, [debouncedOpen, debouncedClose, disableMouseLeave]);

  const handleKeyDown = useCallback((e) => {
    // prevent key presses from bubbling up to parent menus, causing this menu to close or lose focus
    e.stopPropagation();
  }, []);

  const handleSubmenuClose = useCallback(() => {
    debouncedOpen.cancel();
    setIsSubmenuOpen(false);
  }, [debouncedOpen]);

  return (
    <div
      className={cx(styles.wrapper, {
        [styles.open]: isSubmenuOpen,
      })}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onKeyDown={handleKeyDown}
    >
      <InternalListItem
        className={cx(styles.root, className)}
        ref={menuItemRef}
        mode={improveMode}
        labelOverride={labelOverride}
        onClick={handleClick}
        selected={selected}
        disabled={disabled}
        endIcon={
          <ChevronIcon
            className={styles.iconSubmenu}
            direction={ROTATE_DIRECTION.right}
          />
        }
      />

      <Backdrop
        className={styles.backdrop}
        onClick={handleSubmenuClose}
        open={isSubmenuOpen && disableMouseLeave}
        invisible
      />

      <CustomMenu
        className={styles.submenu}
        classes={{ paper: styles.submenu_paper }}
        anchorEl={menuItemRef?.current}
        open={isSubmenuOpen}
        onClose={handleSubmenuClose}
        disabled={disabled}
        {...SUBMENU_PROPS}
      >
        {children({
          closeMenu: handleSubmenuClose,
        })}
      </CustomMenu>
    </div>
  );
};

InternalSubmenuListItem.displayName = 'InternalSubmenuListItem';
InternalSubmenuListItem.propTypes = propTypes;
InternalSubmenuListItem.defaultProps = defaultProps;
